import React, { useState } from 'react';
import { Row, Col, Button, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';

import ScreenshotThumbnail from './ScreenshotThumbnail';
import { c_ScoreColumns, get_score_color_class, get_grade_color_class } from './ScoreRowsTable';

// The sort keys of the name, url, grade and pages columns; a score column sorts by its own key.
const c_NameSortKey = 'name';
const c_UrlSortKey = 'url';
const c_GradeSortKey = 'grade';
const c_PagesSortKey = 'pages';

// The sort keys that compare as text, from A to Z on the first click.
const c_TextSortKeys = [c_NameSortKey, c_UrlSortKey];

// The grades best first, the order a grade sort runs in.
const c_GradeOrder = ['A', 'B', 'C', 'D'];

// The crawl stages a site's pages can be in (Site.crawl_stage), as the backend stores them.
const c_CrawlStageQueued = 'queued';
const c_CrawlStageCapturingHomepage = 'capturing_homepage';
const c_CrawlStageInProgress = 'in_progress';
const c_CrawlStageImportingHistory = 'importing_history';
const c_CrawlStageAnalyzingHistory = 'analyzing_history';
const c_CrawlStageAnalyzing = 'analyzing';
const c_CrawlStageFinished = 'finished';
const c_CrawlStageFailed = 'failed';

// A competitor's role (Site.site_role), as the backend stores it; every other site is one of the user's own.
const c_SiteRoleCompetitor = 'competitor';

const c_CompetitorTagText = 'Competitor';

const c_CapturingHomepageText = 'Capturing homepage';
const c_CrawlingText = 'Crawling pages';
const c_QueuedText = 'Queued';
const c_EditWhileCrawlingText = 'This site can be edited once its pages have been processed.';
const c_ReadingArchiveText = 'Reading archive';
const c_ImportingHistoryText = 'Importing historical pages';
const c_WaitingToImportHistoryText = 'Waiting to import history';
const c_AnalyzingHistoryText = 'Analyzing historical pages';
const c_AnalyzingText = 'Analyzing pages';
const c_CrawlFailedText = 'Pre-processing failed';
export const c_DashboardPendingText = 'The dashboard will be available once the site\'s pages have been crawled and analyzed.';
const c_CompetitorDashboardPendingText = 'The dashboard will be available once the site\'s pages and history have been imported and analyzed.';

// What a site being pre-processed is doing, with its progress, for the site list and the onboarding wizard:
// - queued: Queued;
// - the live homepage screenshot that starts a crawl: Capturing homepage;
// - crawled: Crawling pages (pages recorded of the site's page limit);
// - history import, waiting for the archive import slot: Waiting to import history;
// - history import, reading the archive's capture lists: Reading archive (lists read of lists to read);
// - history import, storing captures: Importing historical pages (captures stored of captures asked for);
// - history analyzed: Analyzing historical pages (pages analyzed of pages to analyze);
// - the site analysis that ends the crawl: Analyzing pages (pages finished of pages covered).
export const get_pre_processing_label = (site)=>{
	if(site.crawl_stage === c_CrawlStageQueued){
		return c_QueuedText;
	}

	if(site.crawl_stage === c_CrawlStageCapturingHomepage){
		return c_CapturingHomepageText;
	}

	if(site.crawl_stage === c_CrawlStageImportingHistory){
		if(site.archive_import_slot !== 1){
			return c_WaitingToImportHistoryText;
		}

		const reading_archive = (site.num_capture_lists_total > 0) && (site.num_capture_lists_read < site.num_capture_lists_total);
		if(reading_archive){
			return c_ReadingArchiveText + ' (' + site.num_capture_lists_read + ' of ' + site.num_capture_lists_total + ')';
		}

		return c_ImportingHistoryText + ' (' + site.num_imported_pages + ((site.num_imported_pages_total > 0) ? ' of ' + site.num_imported_pages_total : '') + ')';
	}

	if(site.crawl_stage === c_CrawlStageAnalyzingHistory){
		const history_progress = site.history_analysis_progress;
		return history_progress ? c_AnalyzingHistoryText + ' (' + history_progress.num_finished + ' of ' + history_progress.num_pages + ')' : c_AnalyzingHistoryText;
	}

	if(site.crawl_stage === c_CrawlStageAnalyzing){
		const progress = site.live_analysis_progress;
		return progress ? c_AnalyzingText + ' (' + progress.num_finished + ' of ' + progress.num_pages + ')' : c_AnalyzingText;
	}

	// The downloads in flight when the limit is reached still complete, so the count is shown no higher than the limit.
	if(site.max_site_pages){
		return c_CrawlingText + ' (' + Math.min(site.num_site_pages, site.max_site_pages) + ' of ' + site.max_site_pages + ')';
	}

	return c_CrawlingText + ' (' + site.num_site_pages + ')';
};

// Whether the site's pages are being pre-processed: queued for a crawl, being crawled, analyzed, or having their
// history imported or analyzed.
export const is_site_pre_processing = (site)=>{
	return is_site_dashboard_pending(site) || is_site_history_running(site);
};

// Whether the site's history is being imported or analyzed.
export const is_site_history_running = (site)=>{
	return (site.crawl_stage === c_CrawlStageImportingHistory) || (site.crawl_stage === c_CrawlStageAnalyzingHistory);
};

// Whether the site's crawl or history import is running (claimed, until it has finished or failed): the site cannot
// be edited then, as the backend refuses it.
export const is_site_crawl_running = (site)=>{
	return (site.crawl_stage === c_CrawlStageCapturingHomepage) ||
		(site.crawl_stage === c_CrawlStageInProgress) ||
		(site.crawl_stage === c_CrawlStageImportingHistory) ||
		(site.crawl_stage === c_CrawlStageAnalyzingHistory) ||
		(site.crawl_stage === c_CrawlStageAnalyzing);
};

// Whether the site's dashboard is not open yet. The user's own site's opens once its live pages are crawled and
// analyzed, while its history is still imported and analyzed; a competitor's only once its import has completed
// altogether: its crawl finished, or failed.
export const is_site_dashboard_pending = (site)=>{
	if(is_competitor_site(site)){
		return (site.crawl_stage !== c_CrawlStageFinished) && (site.crawl_stage !== c_CrawlStageFailed);
	}

	return (site.crawl_stage === c_CrawlStageQueued) ||
		(site.crawl_stage === c_CrawlStageCapturingHomepage) ||
		(site.crawl_stage === c_CrawlStageInProgress) ||
		(site.crawl_stage === c_CrawlStageAnalyzing);
};

// What a site whose dashboard is not open yet is waiting for.
export const get_dashboard_pending_text = (site)=>{
	return is_competitor_site(site) ? c_CompetitorDashboardPendingText : c_DashboardPendingText;
};

// The order competitors are listed in: alphabetical by name, then oldest first, as the backend crawls and imports them.
export const compare_competitors = (first, second)=>{
	const name_order = String(first.title).localeCompare(String(second.title), undefined, { sensitivity: 'base' });
	if(name_order !== 0){
		return name_order;
	}

	return new Date(first.createdAt).getTime() - new Date(second.createdAt).getTime();
};

// Whether the site is a competitor of one of the user's own sites.
export const is_competitor_site = (site)=>{
	return site.site_role === c_SiteRoleCompetitor;
};

// Whether the site failed to be crawled.
export const is_site_crawl_failed = (site)=>{
	return site.crawl_stage === c_CrawlStageFailed;
};

// The user's sites, as a list of card rows in the style of the site dashboard's run list: a header row, then one
// card per site with its thumbnail, name, url, the grade and scores of its latest run, its pages and its buttons.
// Each of the user's own sites is followed by its competitors, indented and tagged. A card opens the site; Edit and
// Delete call back to the view, which owns the site list. A click on a header sorts the sites by its column: grades,
// scores and pages best or most first, names and urls from A to Z, and a second click on the same header reverses
// the order. Until a header is clicked the sites keep the order given.
function SitesTable({sites, on_open, on_edit, on_delete}){
	const [sort_key, set_sort_key] = useState(null);
	const [sort_descending, set_sort_descending] = useState(true);

	const on_header_click = (key)=>{
		if(key === sort_key){
			set_sort_descending(!sort_descending);
			return;
		}

		set_sort_key(key);
		set_sort_descending(!c_TextSortKeys.includes(key));
	};

	// A site's value for a grade, score or pages sort: higher is better; a site without one goes last.
	const get_sort_value = (site, key)=>{
		if(key === c_PagesSortKey){
			return (typeof site.num_site_pages === 'number') ? site.num_site_pages : -1;
		}

		const latest_analysis = site.latest_analysis;
		if(is_site_dashboard_pending(site) || !latest_analysis){
			return -1;
		}

		if(key === c_GradeSortKey){
			const grade_index = c_GradeOrder.indexOf(latest_analysis.grade);
			return (grade_index >= 0) ? (c_GradeOrder.length - grade_index) : -1;
		}

		const value = latest_analysis.scoring ? Number(latest_analysis.scoring[key]) : NaN;
		return isNaN(value) ? -1 : value;
	};

	// A site's text for a name or url sort.
	const get_sort_text = (site, key)=>{
		return (key === c_NameSortKey) ? String(site.title) : strip_url(site.url);
	};

	const compare_sites = (first, second)=>{
		var comparison = 0;
		if(c_TextSortKeys.includes(sort_key)){
			comparison = get_sort_text(first, sort_key).localeCompare(get_sort_text(second, sort_key), undefined, { sensitivity: 'base' });
		}
		else{
			comparison = get_sort_value(first, sort_key) - get_sort_value(second, sort_key);
		}

		return sort_descending ? -comparison : comparison;
	};

	// The sites in display order: each of the user's own sites, in the order given or the sort's, followed by its
	// competitors in competitor order (see compare_competitors) or the sort's. A competitor whose site is not in the
	// list goes at the end.
	const get_ordered_sites = ()=>{
		const ordered_sites = [];
		const main_sites = sites.filter((site)=>{ return !is_competitor_site(site); });
		const competitors = sites.filter((site)=>{ return is_competitor_site(site); });
		competitors.sort(sort_key ? compare_sites : compare_competitors);
		if(sort_key){
			main_sites.sort(compare_sites);
		}

		main_sites.forEach((main_site)=>{
			ordered_sites.push(main_site);
			competitors.forEach((competitor)=>{
				if(competitor.competitor_of_site_id === main_site.id){
					ordered_sites.push(competitor);
				}
			});
		});

		competitors.forEach((competitor)=>{
			if(!ordered_sites.includes(competitor)){
				ordered_sites.push(competitor);
			}
		});

		return ordered_sites;
	};

	const handle_edit = (event, site)=>{
		event.stopPropagation();
		on_edit(site);
	};

	const handle_delete = (event, site)=>{
		event.stopPropagation();
		on_delete(site);
	};

	// The header's text, with an arrow on the column the sites are sorted by.
	const render_header_text = (key, text)=>{
		if(sort_key !== key){
			return text;
		}

		return text + (sort_descending ? ' ▼' : ' ▲');
	};

	const render_header = ()=>{
		return (
			<Row className="sites-header-row">
				<Col className="sites-row-thumbnail-col"></Col>
				<Col
					className="sites-row-text-col sites-header-sortable"
					onClick={()=>{ on_header_click(c_NameSortKey); }}>
					{render_header_text(c_NameSortKey, 'Name')}
				</Col>
				<Col
					className="sites-row-text-col sites-row-url-col sites-header-sortable"
					onClick={()=>{ on_header_click(c_UrlSortKey); }}>
					{render_header_text(c_UrlSortKey, 'Url')}
				</Col>
				<Col
					className="sites-row-score-col sites-row-header-label sites-header-sortable"
					onClick={()=>{ on_header_click(c_GradeSortKey); }}>
					{render_header_text(c_GradeSortKey, 'Grade')}
				</Col>
				{c_ScoreColumns.map((column)=>{
					return (
						<Col
							key={column.key}
							className="sites-row-score-col sites-row-header-label sites-header-sortable"
							onClick={()=>{ on_header_click(column.key); }}>
							{render_header_text(column.key, column.label)}
						</Col>
					);
				})}
				<Col
					className="sites-row-pages-col sites-header-sortable"
					onClick={()=>{ on_header_click(c_PagesSortKey); }}>
					{render_header_text(c_PagesSortKey, 'Pages')}
				</Col>
				<Col className="sites-row-actions-col"></Col>
			</Row>
		);
	};

	// A url as shown in the list: no scheme, no leading www., no trailing slash.
	const strip_url = (url)=>{
		return String(url).replace('https://', '').replace('http://', '').replace(/^www\./i, '').replace(/\/+$/, '');
	};

	// The site's thumbnail: its own, taken from the live homepage when it was crawled, else its latest run's.
	const render_thumbnail = (site)=>{
		let image_id = site.screenshot_thumbnail_image_id ? site.screenshot_thumbnail_image_id : null;
		if(!image_id && site.latest_analysis && site.latest_analysis.screenshot_thumbnail_image_id){
			image_id = site.latest_analysis.screenshot_thumbnail_image_id;
		}

		if(!image_id){
			return null;
		}

		return (
			<ScreenshotThumbnail image_id={image_id} alt_text="Screenshot of the homepage"/>
		);
	};

	// The grade and five scores of the site's latest run, in the dashboard's colours; empty columns without a run, and
	// until its live pages are crawled and analyzed.
	const render_scores = (site)=>{
		const latest_analysis = site.latest_analysis;
		if(is_site_dashboard_pending(site) || !latest_analysis || !latest_analysis.scoring){
			return (
				<React.Fragment>
					<Col className="sites-row-score-col"></Col>
					{c_ScoreColumns.map((column)=>{ return <Col key={column.key} className="sites-row-score-col"></Col>; })}
				</React.Fragment>
			);
		}

		return (
			<React.Fragment>
				<Col className="sites-row-score-col">
					{latest_analysis.grade ? <span className={'sites-row-grade ' + get_grade_color_class(latest_analysis.grade)}>{latest_analysis.grade}</span> : null}
				</Col>
				{c_ScoreColumns.map((column)=>{
					const value = latest_analysis.scoring[column.key];
					return (
						<Col key={column.key} className="sites-row-score-col">
							<span className={'sites-row-score ' + get_score_color_class(value)}>
								{Math.round(value) + '%'}
							</span>
						</Col>
					);
				})}
			</React.Fragment>
		);
	};

	// The pages column: while the site is pre-processed (the view reloads the list while that runs), a spinner with
	// what it is doing and its progress (see get_pre_processing_label), or Queued with no spinner for a competitor
	// waiting its turn. The error when that failed, otherwise the number of pages recorded.
	const render_pages_status = (site)=>{
		// A competitor waiting for its turn in the crawl queue: nothing is being done to it yet, so no spinner.
		if(is_competitor_site(site) && (site.crawl_stage === c_CrawlStageQueued)){
			return (
				<span className="sites-table-queued">
					{c_QueuedText}
				</span>
			);
		}

		if(is_site_pre_processing(site)){
			return (
				<span className="sites-table-pre-processing">
					<Spinner animation="border" size="sm" className="sites-table-pre-processing-spinner"/>
					{get_pre_processing_label(site)}
				</span>
			);
		}

		if(site.crawl_stage === c_CrawlStageFailed){
			return (
				<span className="sites-table-crawl-failed">
					{c_CrawlFailedText}
				</span>
			);
		}

		return (
			<span>
				{site.num_site_pages}
			</span>
		);
	};

	// A site opens its dashboard once its live pages are crawled and analyzed.
	const open_site = (site)=>{
		if(is_site_dashboard_pending(site)){
			return;
		}

		on_open(site);
	};

	// A site whose dashboard is not open yet shows a tooltip saying when it will be, on a click on its row; a click
	// anywhere else closes it.
	const render_row = (site)=>{
		if(!is_site_dashboard_pending(site)){
			return render_row_card(site);
		}

		return (
			<OverlayTrigger
				key={site.id}
				trigger="click"
				placement="top"
				rootClose
				overlay={
					<Tooltip id={'site-dashboard-pending-' + site.id}>
						{get_dashboard_pending_text(site)}
					</Tooltip>
				}>
				{render_row_card(site)}
			</OverlayTrigger>
		);
	};

	const get_row_class = (site)=>{
		var row_class = 'sites-row';
		if(is_site_dashboard_pending(site)){
			row_class = row_class + ' sites-row-processing';
		}

		if(is_competitor_site(site)){
			row_class = row_class + ' sites-row-competitor';
		}

		return row_class;
	};

	// The thumbnail column: a competitor's has the competitor tag above its thumbnail.
	const render_thumbnail_col = (site)=>{
		if(!is_competitor_site(site)){
			return (
				<Col className="sites-row-thumbnail-col">
					{render_thumbnail(site)}
				</Col>
			);
		}

		return (
			<Col className="sites-row-thumbnail-col">
				<div className="sites-row-competitor-tag">{c_CompetitorTagText}</div>
				{render_thumbnail(site)}
			</Col>
		);
	};

	const render_row_card = (site)=>{
		return (
			<Row
				key={site.id}
				className={get_row_class(site)}
				onClick={()=>{ open_site(site); }}>
				{render_thumbnail_col(site)}
				<Col className="sites-row-text-col">{site.title}</Col>
				<Col className="sites-row-text-col sites-row-url-col" title={site.url}>{strip_url(site.url)}</Col>
				{render_scores(site)}
				<Col className="sites-row-pages-col sites-table-pages">
					{render_pages_status(site)}
				</Col>
				<Col className="sites-row-actions-col sites-table-actions">
					<span
						className="sites-table-button-wrapper"
						title={is_site_crawl_running(site) ? c_EditWhileCrawlingText : ''}
						onClick={(event)=>{ event.stopPropagation(); }}>
						<Button
							className="sites-table-button"
							variant="outline-primary"
							size="sm"
							disabled={is_site_crawl_running(site)}
							onClick={(event)=>{ handle_edit(event, site); }}>
							Edit
						</Button>
					</span>
					<Button
						className="sites-table-button"
						variant="outline-danger"
						size="sm"
						onClick={(event)=>{ handle_delete(event, site); }}>
						Delete
					</Button>
				</Col>
			</Row>
		);
	};

	return (
		<div className="sites-rows">
			{render_header()}
			{get_ordered_sites().map((site)=>{ return render_row(site); })}
		</div>
	);
}

export default SitesTable;
